import { z } from "zod";
/** Offline candidate observations only; this is deliberately not fiscal admission. */
export const SELECTED_WORKBOOK_PREPARATION_MAX_BYTES = 256_000;
export const SELECTED_WORKBOOK_MAX_FIELDS = 64;
export const SELECTED_WORKBOOK_MAX_ANCHORS = 128;
/** Commercial presentation roles, not verified source labels or legal classifications. */
export const SELECTED_WORKBOOK_FIELD_KINDS = {
    "context.transactionType": "HASH_ONLY",
    "context.status": "HASH_ONLY",
    "context.category": "HASH_ONLY",
    "context.quantity": "DECIMAL",
    "context.sellerName": "HASH_ONLY",
    "context.sellerLocation": "HASH_ONLY",
    "context.buyerName": "HASH_ONLY",
    "context.buyerLocation": "HASH_ONLY",
    "context.sellerPaymentTerms": "HASH_ONLY",
    "context.buyerPaymentTerms": "HASH_ONLY",
    "cashFlow.salesPrice": "DECIMAL",
    "cashFlow.taxes": "DECIMAL",
    "cashFlow.netRevenue": "DECIMAL",
    "cashFlow.acquisitionPrice": "DECIMAL",
    "cashFlow.grossProfit": "DECIMAL",
    "cashFlow.grossMargin": "DECIMAL",
    "cashFlow.operations": "DECIMAL",
    "cashFlow.thirdPartySalesCommission": "DECIMAL",
    "cashFlow.creditAdvance": "DECIMAL",
    "cashFlow.contributionMargin": "DECIMAL",
    "cashFlow.taxCreditBalance": "DECIMAL",
    "cashFlow.pcPayable": "DECIMAL",
    "cashFlow.icmsPayable": "DECIMAL",
    "cashFlow.icmsStPayable": "DECIMAL",
    "pnl.grossRevenue": "DECIMAL",
    "pnl.taxes": "DECIMAL",
    "pnl.netRevenue": "DECIMAL",
    "pnl.costOfGoodsSold": "DECIMAL",
    "pnl.grossProfit": "DECIMAL",
    "pnl.grossMargin": "DECIMAL",
    "pnl.operations": "DECIMAL",
    "pnl.thirdPartySalesCommission": "DECIMAL",
    "pnl.creditAdvance": "DECIMAL",
    "pnl.contributionMargin": "DECIMAL",
    "pnl.contributionRatio": "DECIMAL",
    "acquisition.netInventory": "DECIMAL",
    "acquisition.pcBasis": "DECIMAL",
    "acquisition.pisCofins": "DECIMAL",
    "acquisition.icmsBasis": "DECIMAL",
    "acquisition.icmsRecoverable": "DECIMAL",
    "acquisition.icmsSt": "DECIMAL",
    "acquisition.invoiceValue": "DECIMAL",
    "acquisition.cost": "DECIMAL",
    "sales.invoiceValue": "DECIMAL",
    "sales.pisCofins": "DECIMAL",
    "sales.icmsRecoverable": "DECIMAL",
    "sales.icmsStRecoverable": "DECIMAL",
};
export const selectedWorkbookFieldIdSchema = z.enum(Object.keys(SELECTED_WORKBOOK_FIELD_KINDS));
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const integer = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const positiveInteger = integer.positive();
const worksheetPart = z.string().regex(/^xl\/worksheets\/sheet[1-9]\d{0,3}\.xml$/);
const address = z
    .string()
    .regex(/^[A-Z]{1,3}[1-9]\d{0,6}$/)
    .refine((value) => {
    const letters = value.replace(/\d+$/, "");
    let column = 0;
    for (const letter of letters)
        column = column * 26 + letter.charCodeAt(0) - 64;
    return column <= 16_384 && Number(value.slice(letters.length)) <= 1_048_576;
});
export const selectedWorkbookAnchorSchema = z.object({ address, contentSha256: sha256 }).strict();
export const selectedWorkbookFieldMappingSchema = z
    .object({
    fieldId: selectedWorkbookFieldIdSchema,
    labelAddress: address,
    valueAddress: address,
    kind: z.enum(["HASH_ONLY", "DECIMAL"]),
})
    .strict()
    .superRefine((field, context) => {
    if (SELECTED_WORKBOOK_FIELD_KINDS[field.fieldId] !== field.kind)
        context.addIssue({ code: "custom", message: "FIELD_KIND_INVALID" });
});
/** Labels may share a heading anchor; value locators must be unique and cannot be anchors. */
export const selectedWorkbookProfileSchema = z
    .object({
    schemaVersion: z.literal("selected-workbook-profile.v1"),
    parentWorkbookSha256: sha256,
    worksheetPart,
    sheetNameSha256: sha256,
    anchorsSha256: sha256,
    anchors: z.array(selectedWorkbookAnchorSchema).min(1).max(SELECTED_WORKBOOK_MAX_ANCHORS),
    fields: z.array(selectedWorkbookFieldMappingSchema).min(1).max(SELECTED_WORKBOOK_MAX_FIELDS),
})
    .strict()
    .superRefine((profile, context) => {
    const anchors = new Set(profile.anchors.map((anchor) => anchor.address));
    const ids = new Set();
    const values = new Set();
    if (anchors.size !== profile.anchors.length)
        context.addIssue({ code: "custom", message: "DUPLICATE_ANCHOR" });
    for (const field of profile.fields) {
        if (ids.has(field.fieldId))
            context.addIssue({ code: "custom", message: "DUPLICATE_FIELD" });
        ids.add(field.fieldId);
        if (values.has(field.valueAddress))
            context.addIssue({ code: "custom", message: "DUPLICATE_ADDRESS" });
        values.add(field.valueAddress);
        if (!anchors.has(field.labelAddress) || anchors.has(field.valueAddress))
            context.addIssue({ code: "custom", message: "ANCHOR_MAPPING_INVALID" });
    }
    if (new TextEncoder().encode(JSON.stringify(profile)).length > 256_000)
        context.addIssue({ code: "custom", message: "PROFILE_TOO_LARGE" });
});
export const selectedWorkbookRejectionCodeSchema = z.enum([
    "CELL_ERROR",
    "FORMULA_SHARED",
    "FORMULA_ARRAY",
    "FORMULA_DATA_TABLE",
    "FORMULA_UNSUPPORTED",
    "CELL_TYPE_UNSUPPORTED",
    "DECIMAL_INVALID",
    "TEXT_FOR_DECIMAL",
]);
export const selectedWorkbookObservationSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("MISSING"), state: z.enum(["ABSENT", "EMPTY"]) }).strict(),
    z
        .object({
        kind: z.literal("DECIMAL"),
        raw: z
            .string()
            .max(64)
            .regex(/^[+-]?\d+(?:\.\d+)?(?:[eE][+-]?\d{1,3})?$/),
        unit: z.literal("UNKNOWN"),
    })
        .strict(),
    z.object({ kind: z.literal("HASH_ONLY"), contentSha256: sha256 }).strict(),
    z
        .object({
        kind: z.literal("FORMULA_UNQUALIFIED"),
        formulaSha256: sha256,
        cacheState: z.enum(["ABSENT", "EMPTY", "VALUE"]),
    })
        .strict(),
    z.object({ kind: z.literal("REJECTED"), code: selectedWorkbookRejectionCodeSchema }).strict(),
]);
export const SELECTED_WORKBOOK_PREPARATION_DIAGNOSTICS = [
    "FORMULAS_NOT_EVALUATED",
    "FORMULA_CACHES_NOT_USED",
    "MONETARY_PARITY_NOT_ESTABLISHED",
    "FISCAL_MAPPING_NOT_ESTABLISHED",
];
const openedPartSchema = z
    .object({
    part: z
        .string()
        .regex(/^(?:\[Content_Types\]\.xml|_rels\/\.rels|xl\/workbook\.xml|xl\/_rels\/workbook\.xml\.rels|xl\/sharedStrings\.xml|xl\/worksheets\/sheet[1-9]\d{0,3}\.xml|xl\/worksheets\/_rels\/sheet[1-9]\d{0,3}\.xml\.rels)$/),
    sha256,
    bytes: positiveInteger,
    compressedBytes: integer,
    declaredBytes: positiveInteger,
    compressionMethod: z.union([z.literal(0), z.literal(8)]),
})
    .strict();
export const selectedWorkbookPreparationSchema = z
    .object({
    schemaVersion: z.literal("selected-workbook-preparation.v1"),
    producerVersion: z.literal("selected-workbook-preparation-v1"),
    state: z.literal("BLOCKED"),
    executable: z.literal(false),
    mappingTrust: z.literal("CANDIDATE"),
    legalValidity: z.literal("UNKNOWN"),
    effectiveFrom: z.null(),
    effectiveTo: z.null(),
    approvedRules: z.literal(0),
    publishedRules: z.literal(0),
    profile: selectedWorkbookProfileSchema,
    provenance: z
        .object({
        sourceBytes: positiveInteger,
        archivedPartCount: positiveInteger,
        declaredUncompressedBytes: positiveInteger,
        worksheetPartSha256: sha256,
        profileSha256: sha256,
        openedBytes: positiveInteger,
        openedParts: z.array(openedPartSchema).min(1).max(8),
    })
        .strict(),
    fields: z
        .array(z
        .object({
        fieldId: selectedWorkbookFieldIdSchema,
        observation: selectedWorkbookObservationSchema,
    })
        .strict())
        .min(1)
        .max(SELECTED_WORKBOOK_MAX_FIELDS),
    diagnostics: z
        .array(z.enum(SELECTED_WORKBOOK_PREPARATION_DIAGNOSTICS))
        .length(SELECTED_WORKBOOK_PREPARATION_DIAGNOSTICS.length),
    coverage: z
        .object({
        scope: z.literal("SELECTED_FIELDS_ONLY"),
        complete: z.literal(false),
        selectedFields: positiveInteger,
        decimalFields: integer,
        hashOnlyFields: integer,
        missingFields: integer,
        formulaFields: integer,
        rejectedFields: integer,
    })
        .strict(),
})
    .strict()
    .superRefine((manifest, context) => {
    const invalid = (message) => context.addIssue({ code: "custom", message });
    const mappings = new Map(manifest.profile.fields.map((field) => [field.fieldId, field]));
    const seen = new Set();
    const counts = { DECIMAL: 0, HASH_ONLY: 0, MISSING: 0, FORMULA_UNQUALIFIED: 0, REJECTED: 0 };
    for (const field of manifest.fields) {
        const mapping = mappings.get(field.fieldId);
        if (!mapping || seen.has(field.fieldId))
            invalid("FIELD_MAPPING_INVALID");
        seen.add(field.fieldId);
        if ((field.observation.kind === "DECIMAL" || field.observation.kind === "HASH_ONLY") &&
            field.observation.kind !== mapping?.kind)
            invalid("OBSERVATION_KIND_INVALID");
        counts[field.observation.kind] += 1;
    }
    if (seen.size !== mappings.size)
        invalid("FIELD_MAPPING_INCOMPLETE");
    const coverage = manifest.coverage;
    if (coverage.selectedFields !== manifest.fields.length ||
        coverage.decimalFields !== counts.DECIMAL ||
        coverage.hashOnlyFields !== counts.HASH_ONLY ||
        coverage.missingFields !== counts.MISSING ||
        coverage.formulaFields !== counts.FORMULA_UNQUALIFIED ||
        coverage.rejectedFields !== counts.REJECTED)
        invalid("COVERAGE_INVALID");
    if (new Set(manifest.diagnostics).size !== SELECTED_WORKBOOK_PREPARATION_DIAGNOSTICS.length)
        invalid("DIAGNOSTICS_INCOMPLETE");
    const parts = manifest.provenance.openedParts;
    if (new Set(parts.map((part) => part.part)).size !== parts.length)
        invalid("DUPLICATE_OPENED_PART");
    const selected = parts.find((part) => part.part === manifest.profile.worksheetPart);
    if (selected?.sha256 !== manifest.provenance.worksheetPartSha256)
        invalid("SELECTED_PART_INVALID");
    const allowedWorksheet = manifest.profile.worksheetPart;
    const allowedRelationships = `${allowedWorksheet.replace("xl/worksheets/", "xl/worksheets/_rels/")}.rels`;
    if (parts.some((part) => part.part.startsWith("xl/worksheets/") &&
        part.part !== allowedWorksheet &&
        part.part !== allowedRelationships))
        invalid("UNSELECTED_PART_OPENED");
    if (parts.some((part) => part.bytes !== part.declaredBytes) ||
        parts.reduce((sum, part) => sum + part.bytes, 0) !== manifest.provenance.openedBytes ||
        manifest.provenance.archivedPartCount < parts.length ||
        manifest.provenance.declaredUncompressedBytes < manifest.provenance.openedBytes ||
        parts.some((part) => part.compressedBytes > manifest.provenance.sourceBytes))
        invalid("PROVENANCE_COUNTS_INVALID");
    if (new TextEncoder().encode(JSON.stringify(manifest)).length >
        SELECTED_WORKBOOK_PREPARATION_MAX_BYTES)
        invalid("MANIFEST_TOO_LARGE");
});
/** ASCII-only contract data: object keys sorted, arrays retain order, no whitespace. */
export function canonicalSelectedWorkbookJson(value) {
    if (Array.isArray(value))
        return `[${value.map(canonicalSelectedWorkbookJson).join(",")}]`;
    if (value !== null && typeof value === "object")
        return `{${Object.entries(value)
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
            .map(([key, item]) => `${JSON.stringify(key)}:${canonicalSelectedWorkbookJson(item)}`)
            .join(",")}}`;
    return JSON.stringify(value);
}
/** Anchor order is lexicographic ASCII address order, not numeric row order. */
export function canonicalSelectedWorkbookAnchors(anchors) {
    return canonicalSelectedWorkbookJson([...anchors].sort((a, b) => (a.address < b.address ? -1 : a.address > b.address ? 1 : 0)));
}
//# sourceMappingURL=selected-workbook-preparation.js.map